import React, { Fragment, useState, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useExport from './useExport';

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

export default function ExportModal({ open, onClose, config, data, companyData }) {
  const { triggerExport, loading } = useExport({ config, data, companyData });
  const [selections, setSelections] = useState({});
  const [dateRanges, setDateRanges] = useState({});

  const filterOptions = useMemo(() => {
    if (!data) return {};
    const result = {};
    config.filters
      .filter(f => f.type === 'multi-select')
      .forEach(f => {
        if (f.companyOptions && companyData) {
          const isNumeric = f.companyOptions.valueKey === 'numericId';
          result[f.label] = companyData
            .filter(c => c['company name'])
            .map(c => ({
              value: isNumeric ? c.id : c['customer id'],
              label: c['company name'],
            }))
            .sort((a, b) => a.label?.localeCompare(b.label));
        } else {
          const values = [
            ...new Set(
              data.map(row => getNestedValue(row, f.field)).filter(v => v != null && v !== '')
            ),
          ].sort();
          result[f.label] = values.map(v => ({ value: v, label: String(v) }));
        }
      });
    return result;
  }, [config, data, companyData]);

  const toggleSelection = (filterLabel, value) => {
    setSelections(prev => {
      const current = prev[filterLabel] || [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [filterLabel]: next };
    });
  };

  const handleDownload = async () => {
    const activeFilters = {};
    config.filters.forEach(f => {
      if (f.type === 'multi-select') {
        activeFilters[f.label] = selections[f.label] || [];
      } else if (f.type === 'date-range') {
        activeFilters[f.label] = dateRanges[f.label] || {};
      }
    });
    await triggerExport(activeFilters);
    onClose();
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-50 inset-0 overflow-y-auto" onClose={loading ? () => {} : onClose}>
        <div className="flex items-center justify-center min-h-screen px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg p-6 z-10">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-semibold text-gray-800">Export {config.entityName}</h3>
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="text-gray-400 hover:text-gray-600 rounded p-1 disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="space-y-5 max-h-[26rem] overflow-y-auto pr-1">
                {config.filters.map(filter => (
                  <div key={filter.label}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {filter.label}
                    </label>

                    {filter.type === 'multi-select' && (
                      <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                        {(filterOptions[filter.label] || []).length === 0 ? (
                          <span className="text-xs text-gray-400 col-span-2">No options available</span>
                        ) : (
                          (filterOptions[filter.label] || []).map(opt => (
                            <label
                              key={String(opt.value)}
                              className="flex items-center gap-1.5 text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
                            >
                              <input
                                type="checkbox"
                                checked={(selections[filter.label] || []).includes(opt.value)}
                                onChange={() => toggleSelection(filter.label, opt.value)}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="truncate text-gray-700">{opt.label}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}

                    {filter.type === 'date-range' && (
                      <div className="flex gap-2">
                        <DatePicker
                          selected={(dateRanges[filter.label] || {}).from || null}
                          onChange={date =>
                            setDateRanges(prev => ({
                              ...prev,
                              [filter.label]: { ...(prev[filter.label] || {}), from: date },
                            }))
                          }
                          placeholderText="From"
                          className="border border-gray-200 rounded px-2 py-1.5 text-sm w-full focus:outline-none focus:border-indigo-400"
                          isClearable
                          dateFormat="MM/dd/yyyy"
                        />
                        <DatePicker
                          selected={(dateRanges[filter.label] || {}).to || null}
                          onChange={date =>
                            setDateRanges(prev => ({
                              ...prev,
                              [filter.label]: { ...(prev[filter.label] || {}), to: date },
                            }))
                          }
                          placeholderText="To"
                          className="border border-gray-200 rounded px-2 py-1.5 text-sm w-full focus:outline-none focus:border-indigo-400"
                          isClearable
                          dateFormat="MM/dd/yyyy"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDownload}
                  disabled={loading || !data}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && (
                    <svg className="animate-spin h-4 w-4 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {loading ? 'Preparing...' : 'Download'}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
